import { Text, TagBadge } from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import type { OrgMember } from '../../hooks/useOrgMembers';
import {
  formatDateTime,
  formatDuration,
  runbookRunStatusColors,
  runbookRunStatusLabels,
  shortSha,
  type RunbookRun,
} from './types';

/** `members` resolves `triggeredBy` (a user id) to a display name; falls back to the raw id when unknown. */
export function runbookRunColumns(members: OrgMember[]): ColumnDef<StockFeatures, RunbookRun>[] {
  const nameByUserId = new Map(members.map((m) => [m.userId, m.displayName ?? m.primaryEmail]));

  return [
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <TagBadge
          name={runbookRunStatusLabels[row.original.status]}
          color={runbookRunStatusColors[row.original.status]}
          hideDot
        />
      ),
    },
    {
      accessorKey: 'slug',
      header: 'Runbook',
      cell: ({ row }) => (
        <Text size="sm" className="font-mono">
          {row.original.slug}
        </Text>
      ),
    },
    {
      accessorKey: 'resolvedSha',
      header: 'Commit',
      cell: ({ row }) => (
        <Text size="sm" className="font-mono text-muted-foreground">
          {shortSha(row.original.resolvedSha)}
        </Text>
      ),
    },
    {
      accessorKey: 'triggeredBy',
      header: 'Triggered By',
      cell: ({ row }) => (
        <Text size="sm">
          {nameByUserId.get(row.original.triggeredBy) ?? row.original.triggeredBy}
        </Text>
      ),
    },
    {
      accessorKey: 'startedAt',
      header: 'Started',
      cell: ({ row }) => (
        <Text size="sm" className="text-muted-foreground">
          {formatDateTime(row.original.startedAt)}
        </Text>
      ),
    },
    {
      id: 'duration',
      header: 'Duration',
      cell: ({ row }) => (
        <Text size="sm">{formatDuration(row.original.startedAt, row.original.finishedAt)}</Text>
      ),
    },
  ];
}
